import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

import { EVENT_TYPES, PURPOSES, type PrintLogFilters } from './types';
import { parseDay } from '@/lib/time';

// Filtros de la bitácora en la URL (UX-NAV-02): la ficha enlaza con ?doc=<id> y un
// enlace compartido reproduce la misma consulta. Un valor inválido se ignora.

const KEYS = ['doc', 'tipo', 'proposito', 'desde', 'hasta', 'folio', 'persona'] as const;
const FOLIO = /^[0-9A-HJKMNP-TV-Z]{10}$/i;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function oneOf<T extends string>(v: string | null, values: readonly T[]): T | null {
  return values.find((x) => x === v) ?? null;
}

export function parseFilters(p: URLSearchParams): PrintLogFilters {
  const doc = p.get('doc');
  const folio = p.get('folio');
  const persona = p.get('persona');
  return {
    doc: doc !== null && /^[1-9]\d{0,18}$/.test(doc) ? doc : null,
    tipo: oneOf(p.get('tipo'), EVENT_TYPES),
    proposito: oneOf(p.get('proposito'), PURPOSES),
    desde: parseDay(p.get('desde')),
    hasta: parseDay(p.get('hasta')),
    folio: folio !== null && FOLIO.test(folio) ? folio.toUpperCase() : null,
    persona: persona !== null && UUID.test(persona) ? persona.toLowerCase() : null,
  };
}

export interface PrintLogUrlState {
  filters: PrintLogFilters;
  active: boolean;
  setFilters: (patch: Partial<PrintLogFilters>) => void;
  clear: () => void;
}

export function usePrintLogUrlState(): PrintLogUrlState {
  const [params, setParams] = useSearchParams();
  const key = params.toString();
  // Objeto estable mientras la URL no cambie (clave de consulta sin renders de más).
  const filters = useMemo(() => parseFilters(new URLSearchParams(key)), [key]);

  const setFilters = useCallback(
    (patch: Partial<PrintLogFilters>) =>
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(patch)) {
            if (v === null || v === '') {
              p.delete(k);
            } else {
              p.set(k, v);
            }
          }
          return p;
        },
        { replace: true },
      ),
    [setParams],
  );

  const clear = useCallback(
    () =>
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          for (const k of KEYS) {
            p.delete(k);
          }
          return p;
        },
        { replace: true },
      ),
    [setParams],
  );

  return { filters, active: KEYS.some((k) => filters[k] !== null), setFilters, clear };
}
